// src/app/worker-types/services/worker-type-file-parser.service.ts
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';

import { WorkerTypeImportRow } from './worker-type-import-row.model';
import { WORKER_TYPE_IMPORT_HEADERS } from '../models/worker-type-import-headers.const';

@Injectable({ providedIn: 'root' })
export class WorkerTypeFileParserService {
  parse(file: File): Observable<WorkerTypeImportRow[]> {
    return new Observable<WorkerTypeImportRow[]>((observer) => {
      try {
        this.validateFileType(file);

        const reader = new FileReader();

        reader.onerror = () => observer.error(new Error('Failed to read file'));

        reader.onload = (e: any) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const rawData = XLSX.utils.sheet_to_json<any[]>(worksheet, {
              header: 1,
              defval: null,
              blankrows: false,
            });

            if (!rawData.length) throw new Error('Uploaded file is empty');

            this.validateHeaders(rawData[0]);

            const rows = this.mapRows(rawData.slice(1));
            observer.next(rows);
            observer.complete();
          } catch (err) {
            observer.error(err);
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (err) {
        observer.error(err);
      }
    });
  }

  private validateFileType(file: File): void {
    const allowed = ['xlsx', 'xls', 'csv'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowed.includes(extension)) {
      throw new Error('Invalid file type. Allowed: xlsx, xls, csv');
    }
  }

  private validateHeaders(headers: any[]): void {
    if (headers.length !== WORKER_TYPE_IMPORT_HEADERS.length) {
      throw new Error('Invalid template: column count mismatch');
    }

    headers.forEach((header, index) => {
      if (header !== WORKER_TYPE_IMPORT_HEADERS[index]) {
        throw new Error(
          `Invalid template: expected "${WORKER_TYPE_IMPORT_HEADERS[index]}" at column ${index + 1}`
        );
      }
    });
  }

  private mapRows(rows: any[][]): WorkerTypeImportRow[] {
    return rows
      .map((row) => {
        const record: any = {};
        WORKER_TYPE_IMPORT_HEADERS.forEach((header, colIndex) => {
          record[header] = this.normalizeValue(row[colIndex]);
        });
        return record as WorkerTypeImportRow;
      })
      .filter((r) => Object.values(r).some((v) => v !== null)); // drop completely empty rows
  }

  private normalizeValue(value: any): any {
    if (value === undefined || value === '') return null;
    return value;
  }
}